import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDOM from 'react-dom';
import { FaPlus, FaTrash, FaExpandArrowsAlt, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { addQuotation, updateQuotation, getQuotationDetails, clearQuotationDetails, getProjects, clearError, clearSuccess, getUnits, addProject, getCustomers, getCostCenters, getTaxes, addCustomer, addTax, addCostCenter, addUnit, addCurrency, getCurrencies } from '../../../../redux/Slices/authSlice';
import { getContractors, clearContractorsError, addContractor, clearContractorsSuccess, setContractors, setContractorsPagination } from '../../../../redux/Slices/contractorsSlice';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import {
  saveDropdownCache, getDropdownCache, generateDropdownCacheKey, getAllDropdownCacheForQuery,
  saveCustomersCache, getCustomersCache, generateCustomersCacheKey, getAllCustomersCacheForQuery,
  isOnline, getAnyDropdownCache
} from '../../../../shared/utils.js/offlineQueue';
import { setProjects, setProjectsPagination, setCustomers, setCustomersPagination, setUnits, setUnitsPagination, setTaxes, setTaxesPagination, setCostCenters, setCostCentersPagination } from '../../../../redux/Slices/authSlice';

const COLORS = {
  primary: '#11324D',
  accent: '#AC2000',
  secondary: '#F6630D',
  neutral: '#B3B3B3',
  light: '#F5F5DC',
};

// Normalize projects API responses to a predictable shape
const normalizeProjectsResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  const data = payload?.data ?? payload;
  let items = [];
  if (Array.isArray(data?.data)) {
    items = data.data;
  } else if (Array.isArray(data)) {
    items = data;
  } else if (data && typeof data === 'object') {
    const possibleArray = Object.values(data).find((val) => Array.isArray(val));
    if (possibleArray) items = possibleArray;
  }

  const meta = data?.meta || payload?.meta || null;
  const pagination = meta
    ? {
      current_page: meta.current_page || 1,
      last_page: meta.last_page || 1,
      per_page: meta.per_page || items.length || 10,
      total: meta.total || items.length || 0,
    }
    : null;

  return { items, pagination };
};

// Build fallback pagination when offline/cache-only
const buildPagination = (items, queryParams = {}, fallback = null) => {
  const perPage = queryParams.per_page || fallback?.per_page || (items?.length || 10);
  const total = fallback?.total || items?.length || 0;
  const lastPage = fallback?.last_page || Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(queryParams.page || fallback?.current_page || 1, lastPage);
  return {
    current_page: currentPage,
    last_page: lastPage,
    per_page: perPage,
    total,
  };
};

// unified add/edit quotation component
const AddQuotation = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { quotationDetails, projects, projectsPagination, isLoading, error, success, units, unitsPagination, customers, customersPagination, costCenters, costCentersPagination, taxes, taxesPagination, currencies, currenciesPagination } = useSelector((s) => s.auth);
  const { list: contractors = [], pagination: contractorsPagination, error: contractorsError, success: contractorsSuccess, isLoading: contractorsLoading } = useSelector((s) => s.contractors);
  const lastErrorRef = useRef({ message: null, time: 0 });

  const [form, setForm] = useState({
    project_id: null,
    customer_id: null, // Changed from contractor_id to customer_id
    subject: '',
    attention_to: '',
    location: '',
    introduction: '',
    valid_until: '',
    payment_terms: '',
    terms_and_conditions: '',
    notes: '',
    discount_percent: 0,
    vat_percent: 0,
    income_tax_percent: 0,
    currency_id: 1,
    exchange_rate: 1.0,
    items: [
      {
        line_no: 1,
        item_type: 'item',
        description_ar: '',
        description_en: '',
        unit_id: null,
        unit_name: null, // Store unit name for display
        qty: 0,
        unit_price: 0,
      },
    ],
  });

  // Type selection: 'contractor' or 'customer'
  const [selectedType, setSelectedType] = useState('customer'); // 'customer' or 'contractor'

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const projectSearchInputRef = useRef(null);
  const projectSearchTimeoutRef = useRef(null);

  const [contractorDropdownOpen, setContractorDropdownOpen] = useState(false);
  const [contractorSearchTerm, setContractorSearchTerm] = useState('');
  const [contractorCurrentPage, setContractorCurrentPage] = useState(1);
  const contractorSearchInputRef = useRef(null);
  const contractorSearchTimeoutRef = useRef(null);

  // Customer/Contractor dropdown (uses customers data for both)
  const [customerContractorDropdownOpen, setCustomerContractorDropdownOpen] = useState(false);
  const [customerContractorSearchTerm, setCustomerContractorSearchTerm] = useState('');
  const [customerContractorCurrentPage, setCustomerContractorCurrentPage] = useState(1);
  const customerContractorSearchInputRef = useRef(null);
  const customerContractorSearchTimeoutRef = useRef(null);

  const [measurableDropdowns, setMeasurableDropdowns] = useState({});
  const [measurableSearchTerms, setMeasurableSearchTerms] = useState({});
  const [measurablePages, setMeasurablePages] = useState({});
  const measurableSearchTimeoutRefs = useRef({});
  const measurableSearchInputRefs = useRef({});

  const [descriptionModal, setDescriptionModal] = useState({ open: false, itemIndex: null, field: null, value: '' });

  // Modals for adding contractor, project, customer, tax, and cost center
  const [showAddContractorModal, setShowAddContractorModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);

  // Contractor form state
  const [contractorForm, setContractorForm] = useState({
    name_ar: '',
    name_en: '',
    tax_number: '',
    address: '',
    phone: '',
    email: '',
    is_active: true
  });

  // Project form state
  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });

  // Customer form state
  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });

  // Tax form state
  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  // Cost center form state
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });

  // Modals for adding contractor, project, customer, tax, and cost center
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [addUnitModalItemIndex, setAddUnitModalItemIndex] = useState(null); // Track which item index opened the modal

  // Unit form state
  const [unitForm, setUnitForm] = useState({
    name_ar: '',
    name_en: '',
    symbol: '',
    is_active: true,
  });

  // Currency form state
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [currencyForm, setCurrencyForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    symbol: '',
    is_base_currency: false,
  });

  // Currency dropdown states
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('');
  const [currencyCurrentPage, setCurrencyCurrentPage] = useState(1);
  const currencySearchInputRef = useRef(null);
  const currencySearchTimeoutRef = useRef(null);

  // Project dropdown states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Project notes textarea ref
  const projectNotesTextareaRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  // Warm up projects cache so dropdown works offline even if it was never opened online
  useEffect(() => {
    const primeProjectsCache = async () => {
      const cachedAny = await getAnyDropdownCache('projects');
      if (Array.isArray(cachedAny) && cachedAny.length > 0) return;
      if (!isOnline()) return;

      const queryParams = { page: 1, per_page: 10 };
      const cacheKey = generateDropdownCacheKey('projects', queryParams);

      try {
        const result = await dispatch(getProjects(queryParams)).unwrap();
        const { items, pagination } = normalizeProjectsResponse(result?.data || result);

        if (items.length > 0) {
          await saveDropdownCache(cacheKey, items, pagination);
          dispatch(setProjects(items));
          if (pagination) dispatch(setProjectsPagination(pagination));
        }
      } catch (err) {
        console.error('Prime projects cache failed', err);
      }
    };

    primeProjectsCache();
  }, [dispatch]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!projectDropdownOpen) return;
      const queryParams = { page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined };
      const cacheKey = generateDropdownCacheKey('projects', queryParams);

      try {
        const cached = await getDropdownCache(cacheKey);
        if (cached?.items) {
          dispatch(setProjects(cached.items));
          dispatch(setProjectsPagination(cached.pagination || buildPagination(cached.items, queryParams)));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getProjects(queryParams)).unwrap();
          const { items, pagination } = normalizeProjectsResponse(result?.data || result);

          if (items.length > 0) {
            await saveDropdownCache(cacheKey, items, pagination);
            dispatch(setProjects(items));
            if (pagination) dispatch(setProjectsPagination(pagination));
          } else {
            console.warn('Could not confirm array structure for projects cache, skipping save.', result);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          const cachedPage = await getDropdownCache(cacheKey);
          if (cachedPage?.items?.length) {
            dispatch(setProjects(cachedPage.items));
            dispatch(setProjectsPagination(cachedPage.pagination || buildPagination(cachedPage.items, queryParams)));
          } else {
            let all = await getAllDropdownCacheForQuery('projects', queryParams);
            if (all.length === 0) {
              all = await getAnyDropdownCache('projects');
            }
            if (all.length > 0) {
              dispatch(setProjects(all));
              dispatch(setProjectsPagination(buildPagination(all, queryParams)));
            }
          }
        }
      } else {
        const cachedPage = await getDropdownCache(cacheKey);
        if (cachedPage?.items?.length) {
          dispatch(setProjects(cachedPage.items));
          dispatch(setProjectsPagination(cachedPage.pagination || buildPagination(cachedPage.items, queryParams)));
        } else {
          let all = await getAllDropdownCacheForQuery('projects', queryParams);
          if (all.length === 0) {
            // Try looser fallback
            all = await getAnyDropdownCache('projects');
          }
          if (all.length > 0) {
            dispatch(setProjects(all));
            dispatch(setProjectsPagination(buildPagination(all, queryParams)));
          }
        }
      }
    };

    // Debounce usage
    if (projectSearchTerm) {
      if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
      projectSearchTimeoutRef.current = setTimeout(() => {
        fetchProjects();
      }, 300);
    } else {
      fetchProjects();
    }

    return () => {
      if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCurrentPage, projectDropdownOpen, projectSearchTerm]);

  useEffect(() => {
    const fetchContractors = async () => {
      if (!contractorDropdownOpen) return;
      const queryParams = { page: contractorCurrentPage, per_page: 10, search: contractorSearchTerm || undefined };
      const cacheKey = generateDropdownCacheKey('contractors', queryParams);

      try {
        const cached = await getDropdownCache(cacheKey);
        if (cached?.items) {
          dispatch(setContractors(cached.items));
          dispatch(setContractorsPagination(cached.pagination));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getContractors(queryParams)).unwrap();
          if (result?.data) {
            await saveDropdownCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          let all = await getAllDropdownCacheForQuery('contractors', queryParams);
          if (all.length === 0) {
            all = await getAnyDropdownCache('contractors');
          }
          if (all.length > 0) dispatch(setContractors(all));
        }
      } else {
        let all = await getAllDropdownCacheForQuery('contractors', queryParams);
        if (all.length === 0) {
          all = await getAnyDropdownCache('contractors');
        }
        if (all.length > 0) dispatch(setContractors(all));
      }
    };
    fetchContractors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorCurrentPage, contractorDropdownOpen, contractorSearchTerm]);

  // Search currencies with debounce
  useEffect(() => {
    if (!currencyDropdownOpen) return;

    if (currencySearchTerm) {
      if (currencySearchTimeoutRef.current) clearTimeout(currencySearchTimeoutRef.current);
      currencySearchTimeoutRef.current = setTimeout(() => {
        setCurrencyCurrentPage(1);
        dispatch(getCurrencies({ page: 1, per_page: 10, search: currencySearchTerm }));
      }, 300);
    } else {
      dispatch(getCurrencies({ page: currencyCurrentPage, per_page: 10 }));
    }

    return () => {
      if (currencySearchTimeoutRef.current) clearTimeout(currencySearchTimeoutRef.current);
    };
  }, [currencySearchTerm, currencyDropdownOpen, currencyCurrentPage, dispatch]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!customerContractorDropdownOpen) return;
      const queryParams = { page: customerContractorCurrentPage, per_page: 10, search: customerContractorSearchTerm || undefined };
      const cacheKey = generateCustomersCacheKey(queryParams);

      try {
        const cached = await getCustomersCache(cacheKey);
        if (cached?.customers) {
          dispatch(setCustomers(cached.customers));
          dispatch(setCustomersPagination(cached.pagination));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getCustomers(queryParams)).unwrap();
          if (result?.data) {
            await saveCustomersCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          const all = await getAllCustomersCacheForQuery(queryParams);
          if (all.length > 0) dispatch(setCustomers(all));
        }
      } else {
        const all = await getAllCustomersCacheForQuery(queryParams);
        if (all.length > 0) dispatch(setCustomers(all));
      }
    };
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerContractorCurrentPage, customerContractorDropdownOpen, customerContractorSearchTerm]);

  // Search customers with debounce
  useEffect(() => {
    if (customerContractorSearchTerm !== undefined && customerContractorDropdownOpen) {
      if (customerContractorSearchTimeoutRef.current) clearTimeout(customerContractorSearchTimeoutRef.current);
      customerContractorSearchTimeoutRef.current = setTimeout(() => {
        setCustomerContractorCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerContractorSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerContractorSearchTimeoutRef.current) clearTimeout(customerContractorSearchTimeoutRef.current);
    };
  }, [customerContractorSearchTerm, customerContractorDropdownOpen, dispatch]);

  useEffect(() => {
    Object.keys(measurableDropdowns).forEach((itemIndex) => {
      if (measurableDropdowns[itemIndex]) {
        const page = measurablePages[itemIndex] || 1;
        const search = measurableSearchTerms[itemIndex] || '';
        const fetchUnits = async () => {
          const queryParams = { page, per_page: 10, search: search || undefined, is_active: true };
          const cacheKey = generateDropdownCacheKey('units', queryParams);

          try {
            const cached = await getDropdownCache(cacheKey);
            if (cached?.items) {
              dispatch(setUnits(cached.items));
              dispatch(setUnitsPagination(cached.pagination));
            }
          } catch (e) { console.error(e); }

          if (isOnline()) {
            try {
              const result = await dispatch(getUnits(queryParams)).unwrap();
              if (result?.data) {
                await saveDropdownCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
              }
            } catch (e) {
              console.error("Online fetch failed, falling back to cache", e);
              let all = await getAllDropdownCacheForQuery('units', queryParams);
              if (all.length === 0) {
                all = await getAnyDropdownCache('units');
              }
              if (all.length > 0) dispatch(setUnits(all));
            }
          } else {
            let all = await getAllDropdownCacheForQuery('units', queryParams);
            if (all.length === 0) {
              all = await getAnyDropdownCache('units');
            }
            if (all.length > 0) dispatch(setUnits(all));
          }
        };
        fetchUnits();
      }
    });
  }, [dispatch, measurableDropdowns, measurablePages]);



  useEffect(() => {
    if (contractorSearchTerm !== undefined && contractorDropdownOpen) {
      if (contractorSearchTimeoutRef.current) clearTimeout(contractorSearchTimeoutRef.current);
      contractorSearchTimeoutRef.current = setTimeout(() => {
        setContractorCurrentPage(1);
        dispatch(getContractors({ page: 1, per_page: 10, search: contractorSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (contractorSearchTimeoutRef.current) clearTimeout(contractorSearchTimeoutRef.current);
    };
  }, [contractorSearchTerm, contractorDropdownOpen, dispatch]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!customerDropdownOpen) return;
      const queryParams = { page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined };
      const cacheKey = generateCustomersCacheKey(queryParams);

      try {
        const cached = await getCustomersCache(cacheKey);
        if (cached?.customers) {
          dispatch(setCustomers(cached.customers));
          dispatch(setCustomersPagination(cached.pagination));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getCustomers(queryParams)).unwrap();
          if (result?.data) {
            await saveCustomersCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          const all = await getAllCustomersCacheForQuery(queryParams);
          if (all.length > 0) dispatch(setCustomers(all));
        }
      } else {
        const all = await getAllCustomersCacheForQuery(queryParams);
        if (all.length > 0) dispatch(setCustomers(all));
      }
    };
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCurrentPage, customerDropdownOpen, customerSearchTerm]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
  }, [customerSearchTerm, customerDropdownOpen, dispatch]);

  useEffect(() => {
    const fetchTaxes = async () => {
      if (!taxDropdownOpen) return;
      const queryParams = { page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined };
      const cacheKey = generateDropdownCacheKey('taxes', queryParams);

      try {
        const cached = await getDropdownCache(cacheKey);
        if (cached?.items) {
          dispatch(setTaxes(cached.items));
          dispatch(setTaxesPagination(cached.pagination));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getTaxes(queryParams)).unwrap();
          if (result?.data) {
            await saveDropdownCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          let all = await getAllDropdownCacheForQuery('taxes', queryParams);
          if (all.length === 0) {
            all = await getAnyDropdownCache('taxes');
          }
          if (all.length > 0) dispatch(setTaxes(all));
        }
      } else {
        let all = await getAllDropdownCacheForQuery('taxes', queryParams);
        if (all.length === 0) {
          all = await getAnyDropdownCache('taxes');
        }
        if (all.length > 0) dispatch(setTaxes(all));
      }
    };
    fetchTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
    };
  }, [taxSearchTerm, taxDropdownOpen, dispatch]);

  useEffect(() => {
    const fetchCostCenters = async () => {
      if (!costCenterDropdownOpen) return;
      const queryParams = { page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined };
      const cacheKey = generateDropdownCacheKey('costCenters', queryParams);

      try {
        const cached = await getDropdownCache(cacheKey);
        if (cached?.items) {
          dispatch(setCostCenters(cached.items));
          dispatch(setCostCentersPagination(cached.pagination));
        }
      } catch (e) { console.error(e); }

      if (isOnline()) {
        try {
          const result = await dispatch(getCostCenters(queryParams)).unwrap();
          if (result?.data) {
            await saveDropdownCache(cacheKey, result.data.data || result.data, result.data.meta || result.meta);
          }
        } catch (e) {
          console.error("Online fetch failed, falling back to cache", e);
          let all = await getAllDropdownCacheForQuery('costCenters', queryParams);
          if (all.length === 0) {
            all = await getAnyDropdownCache('costCenters');
          }
          if (all.length > 0) dispatch(setCostCenters(all));
        }
      } else {
        let all = await getAllDropdownCacheForQuery('costCenters', queryParams);
        if (all.length === 0) {
          all = await getAnyDropdownCache('costCenters');
        }
        if (all.length > 0) dispatch(setCostCenters(all));
      }
    };
    fetchCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
    };
  }, [costCenterSearchTerm, costCenterDropdownOpen, dispatch]);

  useEffect(() => {
    if (success && !showAddProjectModal && !showAddCustomerModal && !showAddTaxModal && !showAddContractorModal && !showAddCostCenterModal && !showAddUnitModal && !showAddCurrencyModal) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      navigate('/quotations');
    } else if (success && showAddCustomerModal) {
      // Success from adding customer - show toast and close customer modal only, keep project modal open
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      setShowAddCustomerModal(false);
      // Project modal should remain open
    } else if (success && showAddCostCenterModal) {
      // Success from adding cost center - show toast only (modal already closed in handleAddCostCenter)
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      // Project modal should remain open
    } else if (success && showAddUnitModal) {
      // Success from adding unit - show toast and close unit modal, refresh units
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      setShowAddUnitModal(false);
      setAddUnitModalItemIndex(null);
      setUnitForm({
        name_ar: '',
        name_en: '',
        symbol: '',
        is_active: true,
      });
      // Refresh units list and reset to first page to show the newly added unit
      dispatch(getUnits({ page: 1, per_page: 10, is_active: true, search: '' }));
      // Reset all measurable dropdowns to first page to ensure new unit appears
      setMeasurablePages({});
      setMeasurableSearchTerms({});
    } else if (success && (showAddProjectModal || showAddTaxModal || showAddContractorModal)) {
      // Don't navigate if any other modal is open - just show success message
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, navigate, showAddProjectModal, showAddCustomerModal, showAddTaxModal, showAddContractorModal, showAddCostCenterModal, showAddUnitModal, showAddCurrencyModal]);

  // Handle contractors success and error
  useEffect(() => {
    if (contractorsSuccess && showAddContractorModal) {
      toast.success(contractorsSuccess, { rtl: true });
      dispatch(clearContractorsSuccess());
      // Close contractor modal on success
      setShowAddContractorModal(false);
      setContractorForm({
        name_ar: '',
        name_en: '',
        tax_number: '',
        address: '',
        phone: '',
        email: '',
        is_active: true
      });
    } else if (contractorsSuccess) {
      toast.success(contractorsSuccess, { rtl: true });
      dispatch(clearContractorsSuccess());
    }
  }, [contractorsSuccess, dispatch, showAddContractorModal]);

  useEffect(() => {
    if (contractorsError && showAddContractorModal) {
      toast.error(contractorsError, { rtl: true });
      dispatch(clearContractorsError());
      // Keep modal open on error
    } else if (contractorsError) {
      toast.error(contractorsError, { rtl: true });
      dispatch(clearContractorsError());
    }
  }, [contractorsError, dispatch, showAddContractorModal]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (contractorsError) {
      toast.error(contractorsError, { rtl: true });
      dispatch(clearContractorsError());
    }
  }, [contractorsError, dispatch]);


  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  // In edit mode: fetch quotation details
  useEffect(() => {
    if (!isEditMode) return;
    dispatch(clearQuotationDetails());
    dispatch(getQuotationDetails(id));
    return () => { dispatch(clearQuotationDetails()); };
  }, [dispatch, id, isEditMode]);

  // In edit mode: populate form from fetched details
  useEffect(() => {
    if (!isEditMode || !quotationDetails) return;
    setForm({
      project_id: quotationDetails.project_id || null,
      customer_id: quotationDetails.customer_id || quotationDetails.contractor_id || null,
      subject: quotationDetails.subject || '',
      attention_to: quotationDetails.attention_to || '',
      location: quotationDetails.location || '',
      introduction: quotationDetails.introduction || '',
      valid_until: quotationDetails.valid_until || '',
      payment_terms: quotationDetails.payment_terms || '',
      terms_and_conditions: quotationDetails.terms_and_conditions || '',
      notes: quotationDetails.notes || '',
      discount_percent: quotationDetails.discount_percent || 0,
      vat_percent: quotationDetails.vat_percent || 0,
      income_tax_percent: quotationDetails.income_tax_percent || 0,
      currency_id: quotationDetails.currency_id || 1,
      exchange_rate: quotationDetails.exchange_rate || 1.0,
      items: quotationDetails.items && quotationDetails.items.length > 0
        ? quotationDetails.items.map((item) => ({
          id: item.id || null,
          line_no: item.line_no || 0,
          item_type: item.item_type || 'item',
          description_ar: item.description_ar || '',
          description_en: item.description_en || '',
          unit_id: item.unit_id || (item.measurable_type === 'unit' ? item.measurable_id : null),
          unit_name: null,
          qty: item.qty || 0,
          unit_price: item.unit_price || 0,
        }))
        : [{ line_no: 1, item_type: 'item', description_ar: '', description_en: '', unit_id: null, unit_name: null, qty: 0, unit_price: 0 }],
    });
    if (quotationDetails.client_label) {
      setSelectedType(quotationDetails.client_label);
    } else if (quotationDetails.contractor_id && !quotationDetails.customer_id) {
      setSelectedType('contractor');
    }
    // Fetch units to display names for existing items
    if (quotationDetails.items && quotationDetails.items.length > 0) {
      dispatch(getUnits({ page: 1, per_page: 100, is_active: true }));
    }
  }, [quotationDetails, isEditMode, dispatch]);

  // Auto-fill form fields when a project is selected
  useEffect(() => {
    if (form.project_id && projects && projects.length > 0) {
      const selectedProject = projects.find(p => String(p.id) === String(form.project_id));
      if (selectedProject) {
        setForm(prev => ({
          ...prev,
          customer_id: selectedProject.customer_id ? String(selectedProject.customer_id) : prev.customer_id,
          location: selectedProject.location || prev.location,
          notes: selectedProject.notes || prev.notes,
        }));

        // Fetch all customers if customer not found to ensure customer dropdown shows the name
        if (selectedProject.customer_id) {
          const customerExists = customers?.find(c => String(c.id) === String(selectedProject.customer_id));
          if (!customerExists) {
            // Fetch customers list to populate dropdown
            dispatch(getCustomers({ page: 1, per_page: 1000, search: '' }));
          }
        }
      }
    }
  }, [form.project_id, projects, dispatch, customers]);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close all dropdowns if clicking outside
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setProjectDropdownOpen(false);
        setContractorDropdownOpen(false);
        setCustomerDropdownOpen(false);
        setCustomerContractorDropdownOpen(false);
        setTaxDropdownOpen(false);
        setCostCenterDropdownOpen(false);
        setMeasurableDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close other dropdowns when opening a new one
  useEffect(() => {
    if (projectDropdownOpen) {
      setContractorDropdownOpen(false);
      setCustomerDropdownOpen(false);
      setCustomerContractorDropdownOpen(false);
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (contractorDropdownOpen) {
      setProjectDropdownOpen(false);
      setCustomerDropdownOpen(false);
      setCustomerContractorDropdownOpen(false);
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [contractorDropdownOpen]);

  useEffect(() => {
    if (customerDropdownOpen) {
      setProjectDropdownOpen(false);
      setContractorDropdownOpen(false);
      setCustomerContractorDropdownOpen(false);
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [customerDropdownOpen]);

  useEffect(() => {
    if (customerContractorDropdownOpen) {
      setProjectDropdownOpen(false);
      setContractorDropdownOpen(false);
      setCustomerDropdownOpen(false);
      setTaxDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [customerContractorDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      setProjectDropdownOpen(false);
      setContractorDropdownOpen(false);
      setCustomerDropdownOpen(false);
      setCustomerContractorDropdownOpen(false);
      setCostCenterDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [taxDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      setProjectDropdownOpen(false);
      setContractorDropdownOpen(false);
      setCustomerDropdownOpen(false);
      setCustomerContractorDropdownOpen(false);
      setTaxDropdownOpen(false);
      setMeasurableDropdowns({});
    }
  }, [costCenterDropdownOpen]);

  // Close other dropdowns when opening a measurable dropdown
  useEffect(() => {
    const openMeasurableKeys = Object.keys(measurableDropdowns).filter(key => measurableDropdowns[key]);
    if (openMeasurableKeys.length > 0) {
      if (projectDropdownOpen) setProjectDropdownOpen(false);
      if (customerContractorDropdownOpen) setCustomerContractorDropdownOpen(false);
      if (contractorDropdownOpen) setContractorDropdownOpen(false);
      if (customerDropdownOpen) setCustomerDropdownOpen(false);
      if (costCenterDropdownOpen) setCostCenterDropdownOpen(false);
      if (taxDropdownOpen) setTaxDropdownOpen(false);
    }
  }, [measurableDropdowns]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, item) => {
      if (item.item_type === 'item') {
        return sum + (Number(item.qty || 0) * Number(item.unit_price || 0));
      }
      return sum;
    }, 0);

    const discountAmount = (subtotal * Number(form.discount_percent || 0)) / 100;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = (afterDiscount * Number(form.vat_percent || 0)) / 100;
    const incomeTaxAmount = (afterDiscount * Number(form.income_tax_percent || 0)) / 100;
    const total = afterDiscount + vatAmount + incomeTaxAmount;

    return {
      subtotal,
      discountAmount,
      afterDiscount,
      vatAmount,
      incomeTaxAmount,
      total
    };
  }, [form.items, form.discount_percent, form.vat_percent, form.income_tax_percent]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    let newValue = value;
    // معالجة خاصة للحقل الرقمي unit_price
    if (field === 'unit_price') {
      // إذا كانت القيمة فارغة، اجعلها فارغة (للسماح للمستخدم بالمسح)
      if (value === '' || value === null) {
        newValue = '';
      } else {
        // تحويل إلى رقم مع منع القيم السالبة
        const parsed = parseFloat(value);
        newValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      }
    }
    newItems[index] = { ...newItems[index], [field]: newValue };
    setForm((prev) => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    const newLineNo = form.items.length > 0 ? Math.max(...form.items.map((item) => item.line_no || 0)) + 1 : 1;
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          line_no: newLineNo,
          item_type: 'item',
          description_ar: '',
          description_en: '',
          unit_id: null,
          qty: 0,
          unit_price: 0,
        },
      ],
    }));
  };

  const removeItem = (index) => {
    if (form.items.length > 1) {
      const newItems = form.items.filter((_, i) => i !== index);
      setForm((prev) => ({ ...prev, items: newItems }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.project_id) {
      toast.error(t('quotations.add.errors.selectProject', { defaultValue: 'برجاء اختيار المشروع' }), { rtl: isRTL });
      return;
    }
    if (form.items.length === 0) {
      toast.error(t('quotations.add.errors.addItems', { defaultValue: 'برجاء إضافة بنود على الأقل' }), { rtl: isRTL });
      return;
    }

    // التحقق من أن جميع البنود من نوع item تحتوي على unit_id
    for (const item of form.items) {
      if (item.item_type === 'item') {
        if (!item.unit_id || item.unit_id === null || item.unit_id === 0) {
          toast.error(t('quotations.add.errors.selectUnit', { defaultValue: 'البند' }) + ` ${item.line_no}: ` + t('quotations.add.errors.mustSelectUnit', { defaultValue: 'يجب اختيار الوحدة' }), { rtl: isRTL });
          return;
        }
      }
    }

    const payload = {
      project_id: form.project_id,
      customer_id: form.customer_id || undefined,
      currency_id: Number(form.currency_id) || 1,
      exchange_rate: Number(form.exchange_rate) || 1.0,
      client_label: selectedType, // 'customer' or 'contractor'
      subject: form.subject || undefined,
      attention_to: form.attention_to || undefined,
      introduction: form.introduction || undefined,
      location: form.location || undefined,
      valid_until: form.valid_until || undefined,
      payment_terms: form.payment_terms || undefined,
      terms_and_conditions: form.terms_and_conditions || undefined,
      notes: form.notes || undefined,
      discount_percent: Number(form.discount_percent) || 0,
      vat_percent: Number(form.vat_percent) || 0,
      income_tax_percent: Number(form.income_tax_percent) || 0,
      items: form.items.map((item) => {
        const itemPayload = {
          line_no: item.line_no,
          item_type: item.item_type || 'item',
          description_ar: item.description_ar || '',
        };
        if (item.description_en) {
          itemPayload.description_en = item.description_en;
        }
        if (item.item_type === 'item') {
          itemPayload.unit_id = item.unit_id;
          if (item.qty !== null && item.qty !== undefined) {
            itemPayload.qty = Number(item.qty);
          }
          if (item.unit_price !== null && item.unit_price !== undefined) {
            itemPayload.unit_price = Number(item.unit_price);
          }
        }
        return itemPayload;
      }),
    };


    const endpoint = isEditMode ? `/quotations/${id}` : '/quotations';
    const method = isEditMode ? 'PUT' : 'POST';
    const successMsg = isEditMode
      ? t('quotations.edit.success', { defaultValue: 'تم تعديل عرض السعر بنجاح' })
      : t('quotations.add.success', { defaultValue: 'تم إضافة عرض السعر بنجاح' });
    const errorMsg = isEditMode
      ? t('quotations.edit.error', { defaultValue: 'فشل تعديل عرض السعر' })
      : t('quotations.add.error', { defaultValue: 'فشل إضافة عرض السعر' });

    makeOfflineRequest(endpoint, method, payload)
      .then(() => {
        toast.success(successMsg, { rtl: isRTL });
        dispatch(clearSuccess());
        navigate('/quotations');
      })
      .catch((error) => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          navigate('/quotations');
        } else {
          const errorData = error.response?.data || error;
          let errorMessage = error.message || errorMsg;

          if (errorData?.errors) {
            errorMessage = Object.values(errorData.errors).flat().join(', ');
          } else if (errorData?.message) {
            errorMessage = errorData.message;
          }

          toast.error(errorMessage, { rtl: isRTL });
        }
      });
  };

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = () => {
    if (!form.project_id) return t('quotations.add.selectProject', { defaultValue: 'اختر المشروع' });
    const selected = (projects || []).find((p) => String(p.id) === String(form.project_id));
    return selected ? renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en') : t('quotations.add.selectProject', { defaultValue: 'اختر المشروع' });
  };

  // Handle contractor form change
  const handleContractorFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContractorForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle project form change
  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    // Save cursor position for textarea
    const isTextarea = e.target.tagName === 'TEXTAREA';
    const cursorPosition = isTextarea ? e.target.selectionStart : null;

    setProjectForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Maintain focus on textarea if it's the notes field
    if (name === 'notes' && projectNotesTextareaRef.current && isTextarea) {
      setTimeout(() => {
        const textarea = projectNotesTextareaRef.current;
        if (textarea) {
          textarea.focus();
          if (cursorPosition !== null) {
            textarea.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 0);
    }
  };

  // Handle customer form change
  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle tax form change
  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle cost center form change
  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle unit form change
  const handleUnitFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUnitForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle currency form change
  const handleCurrencyFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrencyForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle add unit
  const handleAddUnit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!unitForm.name_ar || !unitForm.name_en) {
      toast.error(t('common.fillRequiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    const payload = {
      name_ar: unitForm.name_ar,
      name_en: unitForm.name_en,
      symbol: unitForm.symbol,
      is_active: Boolean(unitForm.is_active),
    };

    makeOfflineRequest('/units', 'POST', payload)
      .then(async (response) => {
        let newUnitId;

        if (response && response.data && response.data.id) {
          // Online case
          newUnitId = response.data.id;
          // Refresh units list
          dispatch(getUnits({ page: 1, per_page: 10, is_active: true }));
        } else {
          // Offline case
          // We queue the request, but we also want to update the UI optimistically
          // Create a fake ID and add it
          newUnitId = `temp_${Date.now()}`;
          const fakeUnit = { id: newUnitId, ...payload };
          dispatch(setUnits([fakeUnit, ...(Array.isArray(units) ? units : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newUnitId && addUnitModalItemIndex !== null) {
          const newItems = [...form.items];
          newItems[addUnitModalItemIndex] = {
            ...newItems[addUnitModalItemIndex],
            unit_id: newUnitId,
            unit_name: isRTL ? payload.name_ar : payload.name_en
          };
          setForm((prev) => ({ ...prev, items: newItems }));
        }
        setShowAddUnitModal(false);
        setUnitForm({
          name_ar: '',
          name_en: '',
          symbol: '',
          is_active: true
        });
      })
      .catch((error) => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          // Should be handled in then block but if it throws
          const newUnitId = `temp_${Date.now()}`;
          const fakeUnit = { id: newUnitId, ...payload };
          dispatch(setUnits([fakeUnit, ...(Array.isArray(units) ? units : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });

          if (addUnitModalItemIndex !== null) {
            const newItems = [...form.items];
            newItems[addUnitModalItemIndex] = {
              ...newItems[addUnitModalItemIndex],
              unit_id: newUnitId,
              unit_name: isRTL ? payload.name_ar : payload.name_en
            };
            setForm((prev) => ({ ...prev, items: newItems }));
          }
          setShowAddUnitModal(false);
          setUnitForm({
            name_ar: '',
            name_en: '',
            symbol: '',
            is_active: true
          });
        } else {
          toast.error(error.message || 'Failed to add unit', { rtl: isRTL });
        }
      });
  };

  // Handle add contractor
  const handleAddContractor = (e) => {
    e.preventDefault();
    if (!contractorForm.name_ar) {
      toast.error(t('common.nameArRequired', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
      return;
    }

    makeOfflineRequest('/contractors', 'POST', contractorForm)
      .then(async (response) => {
        let newId;
        if (response && response.data && response.data.id) {
          newId = response.data.id;
          dispatch(getContractors({ page: 1, per_page: 10 }));
        } else {
          newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...contractorForm };
          dispatch(setContractors([fake, ...(Array.isArray(contractors) ? contractors : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newId) {
          setForm(prev => ({ ...prev, customer_id: newId }));
          setShowAddContractorModal(false);
          setContractorForm({
            name_ar: '',
            name_en: '',
            tax_number: '',
            address: '',
            phone: '',
            email: '',
            is_active: true
          });
        }
      })
      .catch(error => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          const newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...contractorForm };
          dispatch(setContractors([fake, ...(Array.isArray(contractors) ? contractors : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          setForm(prev => ({ ...prev, customer_id: newId }));
          setShowAddContractorModal(false);
          setContractorForm({
            name_ar: '',
            name_en: '',
            tax_number: '',
            address: '',
            phone: '',
            email: '',
            is_active: true
          });
        } else {
          toast.error(error.message || 'Failed to add contractor', { rtl: isRTL });
        }
      });
  };

  // Handle add project
  const handleAddProject = (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('common.fillRequiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    const payload = {
      code: projectForm.code,
      name_ar: projectForm.name_ar,
      name_en: projectForm.name_en || undefined,
      customer_id: projectForm.customer_id || null,
      start_date: projectForm.start_date || undefined,
      end_date: projectForm.end_date || undefined,
      status: projectForm.status,
      contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
      tax_id: projectForm.tax_id || null,
      cost_center_id: projectForm.cost_center_id || null,
      notes: projectForm.notes || undefined,
    };

    makeOfflineRequest('/projects', 'POST', payload)
      .then(async (response) => {
        let newId;
        if (response && response.data && response.data.id) {
          newId = response.data.id;
          dispatch(getProjects({ page: 1, per_page: 10 }));
        } else {
          newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setProjects([fake, ...(Array.isArray(projects) ? projects : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newId) {
          setForm(prev => ({ ...prev, project_id: newId }));
          setShowAddProjectModal(false);
          setProjectForm({
            code: '',
            name_ar: '',
            name_en: '',
            customer_id: null,
            start_date: '',
            end_date: '',
            status: 'planned',
            contract_value: '',
            tax_id: null,
            cost_center_id: null,
            notes: '',
          });
        }
      })
      .catch(error => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          const newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setProjects([fake, ...(Array.isArray(projects) ? projects : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          setForm(prev => ({ ...prev, project_id: newId }));
          setShowAddProjectModal(false);
          setProjectForm({
            code: '',
            name_ar: '',
            name_en: '',
            customer_id: null,
            start_date: '',
            end_date: '',
            status: 'planned',
            contract_value: '',
            tax_id: null,
            cost_center_id: null,
            notes: '',
          });
        } else {
          toast.error(error.message || 'Failed to add project', { rtl: isRTL });
        }
      });
  };

  // Handle add customer
  const handleAddCustomer = (e) => {
    e.preventDefault();
    if (!customerForm.name_ar || !customerForm.name_en) {
      toast.error(t('common.nameBothRequired', { defaultValue: 'برجاء إدخال الاسم بالعربية والإنجليزية' }), { rtl: isRTL });
      return;
    }
    const submitData = {
      ...customerForm,
      is_active: customerForm.is_active ? true : false
    };

    makeOfflineRequest('/customers', 'POST', submitData)
      .then(async (response) => {
        let newId;
        if (response && response.data && response.data.id) {
          newId = response.data.id;
          dispatch(getCustomers({ page: 1, per_page: 10 }));
        } else {
          newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...submitData };
          dispatch(setCustomers([fake, ...(Array.isArray(customers) ? customers : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newId) {
          setForm(prev => ({ ...prev, customer_id: newId }));
          setShowAddCustomerModal(false);
          setCustomerForm({
            name_en: '',
            name_ar: '',
            code: '',
            contact_person: '',
            email: '',
            phone: '',
            address: '',
            tax_number: '',
            is_active: true,
            notes: ''
          });
        }
      })
      .catch(error => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          const newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...submitData };
          dispatch(setCustomers([fake, ...(Array.isArray(customers) ? customers : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          setForm(prev => ({ ...prev, customer_id: newId }));
          setShowAddCustomerModal(false);
          setCustomerForm({
            name_en: '',
            name_ar: '',
            code: '',
            contact_person: '',
            email: '',
            phone: '',
            address: '',
            tax_number: '',
            is_active: true,
            notes: ''
          });
        } else {
          toast.error(error.message || 'Failed to add customer', { rtl: isRTL });
        }
      });
  };

  // Handle add tax
  const handleAddTax = (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || taxForm.rate === '') {
      toast.error(t('common.fillRequiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    const payload = {
      name_ar: taxForm.name_ar,
      name_en: taxForm.name_en,
      rate: Number(taxForm.rate),
      is_active: Boolean(taxForm.is_active),
    };

    makeOfflineRequest('/taxes', 'POST', payload)
      .then(async (response) => {
        let newId;
        if (response && response.data && response.data.id) {
          newId = response.data.id;
          dispatch(getTaxes({ page: 1, per_page: 10 }));
        } else {
          newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setTaxes([fake, ...(Array.isArray(taxes) ? taxes : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newId) {
          setProjectForm(prev => ({ ...prev, tax_id: newId }));
          setShowAddTaxModal(false);
          setTaxForm({
            name_ar: '',
            name_en: '',
            rate: '',
            is_active: true,
          });
          toast.success(t('quotations.add.taxAdded', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
        }
      })
      .catch(error => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          const newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setTaxes([fake, ...(Array.isArray(taxes) ? taxes : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          setProjectForm(prev => ({ ...prev, tax_id: newId }));
          setShowAddTaxModal(false);
          setTaxForm({
            name_ar: '',
            name_en: '',
            rate: '',
            is_active: true,
          });
        } else {
          toast.error(error.message || 'Failed to add tax', { rtl: isRTL });
        }
      });
  };

  // Handle add cost center
  const handleAddCostCenter = (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar || !costCenterForm.name_en) {
      toast.error(t('common.fillRequiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    const payload = {
      name_ar: costCenterForm.name_ar,
      name_en: costCenterForm.name_en,
      code: costCenterForm.code || undefined,
    };

    makeOfflineRequest('/cost-centers', 'POST', payload)
      .then(async (response) => {
        let newId;
        if (response && response.data && response.data.id) {
          newId = response.data.id;
          dispatch(getCostCenters({ page: 1, per_page: 10 }));
        } else {
          newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setCostCenters([fake, ...(Array.isArray(costCenters) ? costCenters : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
        }

        if (newId) {
          setProjectForm(prev => ({ ...prev, cost_center_id: newId }));
          setShowAddCostCenterModal(false);
          setCostCenterForm({
            name_ar: '',
            name_en: '',
            code: '',
          });
        }
      })
      .catch(error => {
        if (error.message === 'OFFLINE_REQUEST_SAVED') {
          const newId = `temp_${Date.now()}`;
          const fake = { id: newId, ...payload };
          dispatch(setCostCenters([fake, ...(Array.isArray(costCenters) ? costCenters : [])]));
          toast.info(t('common.offlineRequestSaved', { defaultValue: 'Request saved offline' }), { rtl: isRTL });
          setProjectForm(prev => ({ ...prev, cost_center_id: newId }));
          setShowAddCostCenterModal(false);
          setCostCenterForm({
            name_ar: '',
            name_en: '',
            code: '',
          });
        } else {
          toast.error(error.message || 'Failed to add cost center', { rtl: isRTL });
        }
      });
  };

  const handleAddCurrency = async (e) => {
    e.preventDefault();
    if (!currencyForm.name_ar || !currencyForm.name_en || !currencyForm.code || !currencyForm.symbol) {
      toast.error(t('currencies.add.fillAllFields', { defaultValue: 'برجاء استكمال جميع الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }

    try {
      const resultAction = await dispatch(addCurrency({
        name_en: currencyForm.name_en,
        name_ar: currencyForm.name_ar,
        code: currencyForm.code.toUpperCase(),
        symbol: currencyForm.symbol,
        is_base_currency: Boolean(currencyForm.is_base_currency),
      }));

      if (addCurrency.fulfilled.match(resultAction)) {
        toast.success(t('currencies.add.success', { defaultValue: 'تم إضافة العملة بنجاح' }), { rtl: isRTL });

        // Get the newly created currency from the result
        const newCurrency = resultAction.payload?.data;
        if (newCurrency && newCurrency.id) {
          // Auto-select the newly added currency in the form
          setForm(prev => ({ ...prev, currency_id: newCurrency.id }));
        }

        setCurrencyForm({
          name_en: '',
          name_ar: '',
          code: '',
          symbol: '',
          is_base_currency: false,
        });

        // Close modal and keep dropdown open
        setShowAddCurrencyModal(false);
        // Reset search and pagination to first page to show the new currency
        setCurrencySearchTerm('');
        setCurrencyCurrentPage(1);
        setCurrencyDropdownOpen(true);

        // Refresh currencies list
        dispatch(getCurrencies({ page: 1, per_page: 100 }));
      }
    } catch (error) {
      toast.error(error.message || t('currencies.add.error', { defaultValue: 'حدث خطأ في إضافة العملة' }), { rtl: isRTL });
    }
  };

  const renderContractorDisplay = () => {
    if (!form.customer_id) return t('quotations.add.selectContractor', { defaultValue: 'اختر المقاول' });
    const selected = (contractors || []).find((c) => String(c.id) === String(form.customer_id));
    if (!selected) return t('quotations.add.selectContractor', { defaultValue: 'اختر المقاول' });
    const name = isRTL ? (selected.name_ar || selected.name_en || '') : (selected.name_en || selected.name_ar || '');
    return name;
  };

  const renderCustomerContractorDisplay = () => {
    if (!form.customer_id) {
      return selectedType === 'contractor' ? t('quotations.add.selectContractor', { defaultValue: 'اختر المقاول' }) : t('quotations.add.selectCustomer', { defaultValue: 'اختر العميل' });
    }
    const selected = (customers || []).find((c) => String(c.id) === String(form.customer_id));
    if (!selected) {
      return selectedType === 'contractor' ? t('quotations.add.selectContractor', { defaultValue: 'اختر المقاول' }) : t('quotations.add.selectCustomer', { defaultValue: 'اختر العميل' });
    }
    const name = isRTL
      ? (selected.name_ar || selected.name || selected.name_en || '')
      : (selected.name_en || selected.name_ar || selected.name || '');
    const code = selected.code || '';
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderCurrencyDisplay = () => {
    if (!form.currency_id) return t('quotations.add.selectCurrency', { defaultValue: 'اختر العملة' });
    const selected = (currencies || []).find((c) => String(c.id) === String(form.currency_id));
    if (!selected) return t('quotations.add.selectCurrency', { defaultValue: 'اختر العملة' });
    return `${selected.code} - ${selected.name_ar}`;
  };

  const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
    // Internal ref fallback if searchInputRef is not provided
    const internalInputRef = useRef(null);
    const inputRef = searchInputRef || internalInputRef;

    // Keep focus on input after state updates
    useEffect(() => {
      if (isOpen && inputRef?.current) {
        const timer = setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [isOpen, searchTerm, items, inputRef]);

    if (!isOpen) return null;

    return (
      <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
        <div style={{ padding: 8 }}>
          <input
            ref={inputRef}
            type="text"
            placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
            value={searchTerm}
            onChange={(e) => {
              onSearchChange(e.target.value);
              // Keep focus after onChange
              setTimeout(() => {
                inputRef.current?.focus({ preventScroll: true });
              }, 0);
            }}
            onFocus={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
          />
        </div>
        {pagination && pagination.last_page >= 1 && (
          <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((page) => (
                  <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                ));
              })()}
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
            </div>
          </div>
        )}
        <div>
          {(Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(item.id);
              }}
              style={{ padding: 10, cursor: 'pointer', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <span style={{ color: 'white' }}>{renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}</span>
            </div>
          ))}
          {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', width: '100%', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
        <h1 style={{ margin: 0, fontSize: '20px', textAlign: isRTL ? 'right' : 'left' }}>
          {isEditMode ? t('quotations.edit.title', { defaultValue: 'تعديل عرض سعر' }) : t('quotations.add.title', { defaultValue: 'إضافة عرض سعر' })}
        </h1>
        <button type="button" onClick={() => navigate('/quotations')} style={{ backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px' }}>
          {t('btns.back', { defaultValue: 'رجوع' })}
        </button>
      </div>
      <form onSubmit={handleSubmit} style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 24, display: 'grid', gap: 16, width: '100%', boxSizing: 'border-box', overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.project', { defaultValue: 'المشروع' })} <span style={{ color: '#dc3545' }}>*</span></label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div data-dropdown-toggle onClick={() => { setProjectDropdownOpen((p) => !p); if (projectDropdownOpen) setProjectSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                <span>{renderProjectDisplay()}</span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <button
                type="button"
                onClick={() => setShowAddProjectModal(true)}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  height: '44px'
                }}
                title={t('quotations.add.addProject', { defaultValue: 'إضافة مشروع جديد' })}
              >
                <FaPlus />
              </button>
            </div>
            <DropdownWithSearch isOpen={projectDropdownOpen} onToggle={setProjectDropdownOpen} searchTerm={projectSearchTerm} onSearchChange={setProjectSearchTerm} searchInputRef={projectSearchInputRef} items={Array.isArray(projects) ? projects : []} pagination={projectsPagination} currentPage={projectCurrentPage} onPageChange={setProjectCurrentPage} onSelect={(id) => { setForm((prev) => ({ ...prev, project_id: id })); setProjectDropdownOpen(false); setProjectSearchTerm(''); setProjectCurrentPage(1); }} displayValue={renderProjectDisplay} placeholder={t('quotations.add.selectProject', { defaultValue: 'اختر المشروع' })} label={t('quotations.add.project', { defaultValue: 'مشروع' })} />
          </div>
          {/* Type Selection: Customer or Contractor */}
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.type', { defaultValue: 'النوع' })}</label>
            <div style={{ display: 'flex', gap: '8px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, padding: '4px' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedType('customer');
                  setForm((prev) => ({ ...prev, customer_id: null }));
                  setCustomerContractorDropdownOpen(false);
                  setCustomerContractorSearchTerm('');
                  setCustomerContractorCurrentPage(1);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: selectedType === 'customer' ? '#AC2000' : 'transparent',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t('quotations.add.customer', { defaultValue: 'عميل' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedType('contractor');
                  setForm((prev) => ({ ...prev, customer_id: null }));
                  setCustomerContractorDropdownOpen(false);
                  setCustomerContractorSearchTerm('');
                  setCustomerContractorCurrentPage(1);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: selectedType === 'contractor' ? '#AC2000' : 'transparent',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t('quotations.add.contractor', { defaultValue: 'مقاول' })}
              </button>
            </div>
          </div>
          {/* Customer/Contractor Dropdown (uses customers data for both) */}
          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              {selectedType === 'contractor' ? t('quotations.add.contractor', { defaultValue: 'المقاول' }) : t('quotations.add.customer', { defaultValue: 'العميل' })}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div
                data-dropdown-toggle
                onClick={() => {
                  if (!form.project_id) {
                    setCustomerContractorDropdownOpen((p) => !p);
                    if (customerContractorDropdownOpen) setCustomerContractorSearchTerm('');
                  }
                }}
                style={{
                  flex: 1,
                  padding: 12,
                  backgroundColor: form.project_id ? '#0d1117' : '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  color: form.project_id ? '#666' : 'white',
                  fontSize: 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: form.project_id ? 'not-allowed' : 'pointer',
                  boxSizing: 'border-box',
                  opacity: form.project_id ? 0.6 : 1
                }}
              >
                <span>{renderCustomerContractorDisplay()}</span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!form.project_id) {
                    setShowAddCustomerModal(true);
                  }
                }}
                disabled={!!form.project_id}
                style={{
                  backgroundColor: form.project_id ? '#4a0000' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  cursor: form.project_id ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  height: '44px',
                  opacity: form.project_id ? 0.6 : 1
                }}
                title={selectedType === 'contractor' ? t('quotations.add.addContractor', { defaultValue: 'إضافة مقاول جديد' }) : t('quotations.add.addCustomer', { defaultValue: 'إضافة عميل جديد' })}
              >
                <FaPlus />
              </button>
            </div>
            {!form.project_id && (
              <DropdownWithSearch
                isOpen={customerContractorDropdownOpen}
                onToggle={setCustomerContractorDropdownOpen}
                searchTerm={customerContractorSearchTerm}
                onSearchChange={setCustomerContractorSearchTerm}
                searchInputRef={customerContractorSearchInputRef}
                items={Array.isArray(customers) ? customers : []}
                pagination={customersPagination}
                currentPage={customerContractorCurrentPage}
                onPageChange={setCustomerContractorCurrentPage}
                onSelect={(id) => {
                  setForm((prev) => ({ ...prev, customer_id: id }));
                  setCustomerContractorDropdownOpen(false);
                  setCustomerContractorSearchTerm('');
                  setCustomerContractorCurrentPage(1);
                }}
                displayValue={renderCustomerContractorDisplay}
                placeholder={selectedType === 'contractor' ? t('quotations.add.selectContractor', { defaultValue: 'اختر المقاول' }) : t('quotations.add.selectCustomer', { defaultValue: 'اختر العميل' })}
                label={selectedType === 'contractor' ? t('quotations.add.contractor', { defaultValue: 'مقاول' }) : t('quotations.add.customer', { defaultValue: 'عميل' })}
              />
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.subject', { defaultValue: 'الموضوع' })}</label>
            <input type="text" name="subject" value={form.subject} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.subjectPlaceholder', { defaultValue: 'عرض سعر أعمال إنشائية' })} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.attentionTo', { defaultValue: 'الاهتمام إلى' })}</label>
            <input type="text" name="attention_to" value={form.attention_to} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.attentionToPlaceholder', { defaultValue: 'السيد / مدير المشروع' })} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.location', { defaultValue: 'الموقع' })}</label>
            <input type="text" name="location" value={form.location} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.locationPlaceholder', { defaultValue: 'الرياض' })} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.validUntil', { defaultValue: 'صالح حتى' })}</label>
            <input type="date" name="valid_until" value={form.valid_until} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.currency', { defaultValue: 'العملة' })}</label>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                data-dropdown-toggle
                onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
                style={{
                  width: '100%',
                  padding: 12,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr',
                  boxSizing: 'border-box'
                }}>
                <span>{renderCurrencyDisplay()}</span>
                <span style={{ fontSize: 12 }}>▼</span>
              </button>
              <DropdownWithSearch
                isOpen={currencyDropdownOpen}
                onToggle={setCurrencyDropdownOpen}
                searchTerm={currencySearchTerm}
                onSearchChange={setCurrencySearchTerm}
                searchInputRef={currencySearchInputRef}
                items={Array.isArray(currencies) ? currencies : []}
                pagination={currenciesPagination}
                currentPage={currencyCurrentPage}
                onPageChange={setCurrencyCurrentPage}
                onSelect={(id) => {
                  setForm((prev) => ({ ...prev, currency_id: id }));
                  setCurrencyDropdownOpen(false);
                  setCurrencySearchTerm('');
                  setCurrencyCurrentPage(1);
                }}
                displayValue={renderCurrencyDisplay}
                placeholder={t('quotations.add.selectCurrency', { defaultValue: 'اختر العملة' })}
                label={t('quotations.add.currency', { defaultValue: 'العملة' })}
                renderItem={(item) => `${item.code} - ${item.name_ar}`}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.exchangeRate', { defaultValue: 'سعر الصرف' })}</label>
            <input type="number" name="exchange_rate" value={form.exchange_rate} onChange={handleChange} onWheel={(e) => e.target.blur()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.exchangeRatePlaceholder', { defaultValue: '1.0' })} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.discountPercent', { defaultValue: 'نسبة الخصم' })}</label>
            <div style={{ position: 'relative' }}>
              <input type="number" name="discount_percent" value={form.discount_percent} onChange={handleChange} onWheel={(e) => e.target.blur()} style={{ width: '100%', padding: 12, paddingRight: isRTL ? '35px' : '35px', paddingLeft: isRTL ? '12px' : '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
              <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '12px', top: '50%', transform: 'translateY(-50%)', color: '#AC2000', fontSize: '14px', fontWeight: 'bold', pointerEvents: 'none' }}>%</span>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.vatPercent', { defaultValue: 'نسبة الضريبة' })}</label>
            <div style={{ position: 'relative' }}>
              <input type="number" name="vat_percent" value={form.vat_percent} onChange={handleChange} onWheel={(e) => e.target.blur()} style={{ width: '100%', padding: 12, paddingRight: isRTL ? '35px' : '35px', paddingLeft: isRTL ? '12px' : '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
              <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '12px', top: '50%', transform: 'translateY(-50%)', color: '#AC2000', fontSize: '14px', fontWeight: 'bold', pointerEvents: 'none' }}>%</span>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.incomeTaxPercent', { defaultValue: 'نسبة ضريبة الدخل' })}</label>
            <div style={{ position: 'relative' }}>
              <input type="number" name="income_tax_percent" value={form.income_tax_percent} onChange={handleChange} onWheel={(e) => e.target.blur()} style={{ width: '100%', padding: 12, paddingRight: isRTL ? '35px' : '35px', paddingLeft: isRTL ? '12px' : '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
              <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '12px', top: '50%', transform: 'translateY(-50%)', color: '#AC2000', fontSize: '14px', fontWeight: 'bold', pointerEvents: 'none' }}>%</span>
            </div>
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.introduction', { defaultValue: 'المقدمة' })}</label>
          <textarea name="introduction" value={form.introduction} onChange={handleChange} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.introductionPlaceholder', { defaultValue: 'يسرنا أن نقدم لكم عرضنا التالي...' })} />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.paymentTerms', { defaultValue: 'شروط الدفع' })}</label>
          <textarea name="payment_terms" value={form.payment_terms} onChange={handleChange} rows={2} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.paymentTermsPlaceholder', { defaultValue: '50% مقدم والباقي عند الاستلام' })} />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.termsAndConditions', { defaultValue: 'الشروط والأحكام' })}</label>
          <textarea name="terms_and_conditions" value={form.terms_and_conditions} onChange={handleChange} rows={2} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} placeholder={t('quotations.add.termsAndConditionsPlaceholder', { defaultValue: 'تطبق الشروط والأحكام العامة' })} />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <label style={{ fontSize: 14, fontWeight: 600, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.items', { defaultValue: 'البنود' })}</label>
            <button type="button" onClick={addItem} style={{ backgroundColor: COLORS.secondary, color: 'white', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, direction: isRTL ? 'rtl' : 'ltr' }}>
              <FaPlus /> {t('quotations.add.addItem', { defaultValue: 'إضافة بند' })}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {form.items.map((item, index) => {
              const itemType = item.item_type || 'item';
              const total = itemType === 'item' ? (Number(item.qty || 0) * Number(item.unit_price || 0)) : 0;
              const isItem = itemType === 'item';
              const isSection = itemType === 'section';
              const isNote = itemType === 'note';
              return (
                <div key={index} style={{
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  padding: '20px',
                  position: 'relative'
                }}>
                  {/* الصف الأول */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
                    {/* رقم البند */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.itemNo', { defaultValue: 'رقم البند' })}
                      </label>
                      <input
                        type="number"
                        value={item.line_no}
                        onChange={(e) => handleItemChange(index, 'line_no', parseInt(e.target.value) || 0)}
                        onWheel={(e) => e.target.blur()}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#1b3350',
                          border: '1px solid #333',
                          borderRadius: 6,
                          color: 'white',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* النوع */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.itemType', { defaultValue: 'النوع' })}
                      </label>
                      <select
                        value={itemType}
                        onChange={(e) => handleItemChange(index, 'item_type', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#1b3350',
                          border: '1px solid #333',
                          borderRadius: 6,
                          color: 'white',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          direction: isRTL ? 'rtl' : 'ltr',
                          textAlign: isRTL ? 'right' : 'left'
                        }}
                      >
                        <option value="section">{t('quotations.add.itemTypeSection', { defaultValue: 'قسم' })}</option>
                        <option value="item">{t('quotations.add.itemTypeItem', { defaultValue: 'بند' })}</option>
                        <option value="note">{t('quotations.add.itemTypeNote', { defaultValue: 'ملاحظة' })}</option>
                      </select>
                    </div>

                    {/* الوصف بالعربية */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.descriptionAr', { defaultValue: 'الوصف (عربي)' })}
                      </label>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        <input
                          type="text"
                          value={item.description_ar}
                          onChange={(e) => handleItemChange(index, 'description_ar', e.target.value)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#1b3350',
                            border: '1px solid #333',
                            borderRadius: 6,
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                          placeholder={t('quotations.add.descriptionArPlaceholder', { defaultValue: 'الوصف بالعربية' })}
                        />
                        <button
                          type="button"
                          onClick={() => setDescriptionModal({ open: true, itemIndex: index, field: 'description_ar', value: item.description_ar || '' })}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: COLORS.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                          title={t('quotations.add.expand', { defaultValue: 'توسيع' })}
                        >
                          <FaExpandArrowsAlt />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* الصف الثاني */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
                    {/* الوصف بالإنجليزية */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.descriptionEn', { defaultValue: 'الوصف (إنجليزي)' })}
                      </label>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        <input
                          type="text"
                          value={item.description_en}
                          onChange={(e) => handleItemChange(index, 'description_en', e.target.value)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#1b3350',
                            border: '1px solid #333',
                            borderRadius: 6,
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: isRTL ? 'rtl' : 'ltr',
                            textAlign: isRTL ? 'right' : 'left'
                          }}
                          placeholder={t('quotations.add.descriptionEnPlaceholder', { defaultValue: 'الوصف بالإنجليزية' })}
                        />
                        <button
                          type="button"
                          onClick={() => setDescriptionModal({ open: true, itemIndex: index, field: 'description_en', value: item.description_en || '' })}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: COLORS.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                          title={t('quotations.add.expand', { defaultValue: 'توسيع' })}
                        >
                          <FaExpandArrowsAlt />
                        </button>
                      </div>
                    </div>

                    {/* الوحدة */}
                    <div style={{ position: 'relative' }} data-dropdown-container>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.unit', { defaultValue: 'الوحدة' })}
                      </label>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <div
                          data-dropdown-toggle
                          onClick={() => {
                            if (!measurableDropdowns[index]) {
                              // Close all other dropdowns explicitly
                              setProjectDropdownOpen(false);
                              setContractorDropdownOpen(false);
                              setCustomerDropdownOpen(false);
                              setCustomerContractorDropdownOpen(false);
                              setTaxDropdownOpen(false);
                              setCostCenterDropdownOpen(false);
                              setMeasurableDropdowns({}); // Close other unit dropdowns

                              // Open this dropdown
                              setMeasurableDropdowns((prev) => ({ ...prev, [index]: true }));

                              // Reset search and page when opening dropdown
                              setMeasurableSearchTerms((prev) => ({ ...prev, [index]: '' }));
                              setMeasurablePages((prev) => ({ ...prev, [index]: 1 }));
                              dispatch(getUnits({ page: 1, per_page: 10, is_active: true, search: '' }));
                            } else {
                              // Close this dropdown
                              setMeasurableDropdowns((prev) => ({ ...prev, [index]: false }));
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: isItem ? '#1b3350' : '#0f1419',
                            border: '1px solid #333',
                            borderRadius: 6,
                            color: isItem ? 'white' : '#666',
                            fontSize: '14px',
                            cursor: isItem ? 'pointer' : 'not-allowed',
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                            {(() => {
                              if (!item.unit_id) return t('quotations.add.selectUnit', { defaultValue: 'اختر الوحدة' });
                              const u = units.find((u) => u.id === item.unit_id)
                                || (isEditMode && quotationDetails?.items?.find((it) => it.id === item.id)?.unit);
                              if (!u) return item.unit_name || t('quotations.add.selectUnit', { defaultValue: 'اختر الوحدة' });
                              if (u.display_name) return u.display_name;
                              const name = isRTL ? (u.name_ar || u.name_en || '') : (u.name_en || u.name_ar || '');
                              return u.symbol ? `${name} (${u.symbol})` : name || t('quotations.add.selectUnit', { defaultValue: 'اختر الوحدة' });
                            })()}
                          </span>
                          <span style={{ fontSize: 12 }}>▼</span>
                        </div>
                        {isItem && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddUnitModalItemIndex(index);
                              setShowAddUnitModal(true);
                            }}
                            style={{
                              padding: '10px 12px',
                              backgroundColor: '#AC2000',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                            title={t('quotations.add.modals.addUnit.title', { defaultValue: 'إضافة وحدة جديدة' })}
                          >
                            <FaPlus />
                          </button>
                        )}
                      </div>
                      {isItem && measurableDropdowns[index] && (
                        <DropdownWithSearch
                          isOpen={measurableDropdowns[index]}
                          onToggle={(open) => setMeasurableDropdowns((prev) => ({ ...prev, [index]: open }))}
                          searchTerm={measurableSearchTerms[index] || ''}
                          onSearchChange={(val) => {
                            setMeasurableSearchTerms((prev) => ({ ...prev, [index]: val }));
                            if (measurableSearchTimeoutRefs.current[index]) clearTimeout(measurableSearchTimeoutRefs.current[index]);
                            measurableSearchTimeoutRefs.current[index] = setTimeout(() => {
                              setMeasurablePages((prev) => ({ ...prev, [index]: 1 }));
                              dispatch(getUnits({ page: 1, per_page: 10, search: val || undefined, is_active: true }));
                            }, 300);
                          }}
                          searchInputRef={null}
                          items={Array.isArray(units) ? units : []}
                          pagination={unitsPagination}
                          currentPage={measurablePages[index] || 1}
                          onPageChange={(page) => {
                            setMeasurablePages((prev) => ({ ...prev, [index]: page }));
                            dispatch(getUnits({ page, per_page: 10, search: measurableSearchTerms[index] || undefined, is_active: true }));
                          }}
                          onSelect={(id) => {
                            // Find the selected unit from current units array to get its name
                            const selectedUnit = (Array.isArray(units) ? units : []).find((u) => String(u.id) === String(id));
                            const unitName = selectedUnit
                              ? (selectedUnit.display_name || (selectedUnit.symbol ? `${isRTL ? (selectedUnit.name_ar || selectedUnit.name_en) : (selectedUnit.name_en || selectedUnit.name_ar)} (${selectedUnit.symbol})` : (isRTL ? (selectedUnit.name_ar || selectedUnit.name_en || '') : (selectedUnit.name_en || selectedUnit.name_ar || ''))))
                              : null;

                            const newItems = [...form.items];
                            newItems[index] = {
                              ...newItems[index],
                              unit_id: id,
                              unit_name: unitName
                            };
                            setForm((prev) => ({ ...prev, items: newItems }));
                            setMeasurableDropdowns((prev) => ({ ...prev, [index]: false }));
                            setMeasurableSearchTerms((prev) => ({ ...prev, [index]: '' }));
                            setMeasurablePages((prev) => ({ ...prev, [index]: 1 }));
                          }}
                          displayValue={() => ''}
                          placeholder={t('quotations.add.selectUnit', { defaultValue: 'اختر الوحدة' })}
                          label={t('quotations.add.unit', { defaultValue: 'وحدة' })}
                          renderItem={(u) => {
                            if (!u) return '';
                            if (typeof u.display_name === 'string') return u.display_name;
                            if (u.symbol) {
                              const name = isRTL ? (u.name_ar || u.name_en) : (u.name_en || u.name_ar);
                              return name ? `${name} (${u.symbol})` : '';
                            }
                            return isRTL ? (u.name_ar || u.name_en || '') : (u.name_en || u.name_ar || '');
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* الصف الثالث */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
                    {/* الكمية */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.quantity', { defaultValue: 'الكمية' })}
                      </label>
                      <input
                        type="number"

                        value={item.qty || ''}
                        onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        disabled={!isItem}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: isItem ? '#1b3350' : '#0f1419',
                          border: '1px solid #333',
                          borderRadius: 6,
                          color: isItem ? 'white' : '#666',
                          fontSize: '14px',
                          cursor: isItem ? 'text' : 'not-allowed',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* سعر الوحدة */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: 'white', fontWeight: '500', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                        {t('quotations.add.unitPrice', { defaultValue: 'سعر الوحدة' })}
                      </label>
                      <input
                        type="number"

                        value={item.unit_price || ''}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        disabled={!isItem}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: isItem ? '#1b3350' : '#0f1419',
                          border: '1px solid #333',
                          borderRadius: 6,
                          color: isItem ? 'white' : '#666',
                          fontSize: '14px',
                          cursor: isItem ? 'text' : 'not-allowed',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  {/* زر الحذف */}
                  {form.items.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={{
                          backgroundColor: '#c0392b',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          padding: '10px 30px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {t('btns.delete', { defaultValue: 'حذف' })}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{
            backgroundColor: '#1a1f2e',
            border: '2px solid #AC2000',
            borderRadius: 8,
            padding: 20,
            marginBottom: 20,
            boxShadow: '0 4px 6px rgba(172, 32, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}>
              <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#fff',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('quotations.add.totalAmount', { defaultValue: 'الإجمالي الكلي' })}
              </div>
              <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#fff',
                direction: 'ltr'
              }}>
                {totals.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.notes', { defaultValue: 'ملاحظات' })}</label>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <button type="button" onClick={() => navigate('/quotations')} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>{t('btns.cancel', { defaultValue: 'إلغاء' })}</button>
          <button type="submit" disabled={isLoading} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>{isLoading ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('btns.save', { defaultValue: 'حفظ' })}</button>
        </div>
      </form >

      {
        descriptionModal.open && ReactDOM.createPortal(
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setDescriptionModal({ open: false, itemIndex: null, field: null, value: '' });
              }
            }}
          >
            <div style={{ background: '#1b3350', borderRadius: 12, padding: 24, minWidth: 600, maxWidth: 800, maxHeight: '80vh', border: '1px solid #333' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: 16, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                {descriptionModal.field === 'description_ar' ? t('quotations.add.descriptionAr', { defaultValue: 'الوصف بالعربية' }) : t('quotations.add.descriptionEn', { defaultValue: 'الوصف بالإنجليزية' })}
              </h3>
              <textarea
                value={descriptionModal.value}
                onChange={(e) => setDescriptionModal((prev) => ({ ...prev, value: e.target.value }))}
                style={{
                  width: '100%',
                  minHeight: 300,
                  padding: 12,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                placeholder={descriptionModal.field === 'description_ar' ? t('quotations.add.descriptionArPlaceholder', { defaultValue: 'اكتب الوصف بالعربية...' }) : t('quotations.add.descriptionEnPlaceholder', { defaultValue: 'Write description in English...' })}
              />
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  onClick={() => setDescriptionModal({ open: false, itemIndex: null, field: null, value: '' })}
                  style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}
                >
                  {t('btns.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  onClick={() => {
                    if (descriptionModal.itemIndex !== null && descriptionModal.field) {
                      handleItemChange(descriptionModal.itemIndex, descriptionModal.field, descriptionModal.value);
                    }
                    setDescriptionModal({ open: false, itemIndex: null, field: null, value: '' });
                  }}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}
                >
                  {t('btns.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Modal لإضافة مقاول جديد */}
      {
        showAddContractorModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }} onClick={() => setShowAddContractorModal(false)}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>إضافة مقاول جديد</h2>
                <button
                  onClick={() => {
                    setShowAddContractorModal(false);
                    setContractorForm({
                      name_ar: '',
                      name_en: '',
                      tax_number: '',
                      address: '',
                      phone: '',
                      email: '',
                      is_active: true
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddContractor}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      الاسم بالعربية *
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={contractorForm.name_ar}
                      onChange={handleContractorFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      الاسم بالإنجليزية
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={contractorForm.name_en}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      الرقم الضريبي
                    </label>
                    <input
                      type="text"
                      name="tax_number"
                      value={contractorForm.tax_number}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      العنوان
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={contractorForm.address}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      الهاتف
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={contractorForm.phone}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contractorForm.email}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={contractorForm.is_active}
                      onChange={handleContractorFormChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                      نشط
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddContractorModal(false);
                      setContractorForm({
                        name_ar: '',
                        name_en: '',
                        tax_number: '',
                        address: '',
                        phone: '',
                        email: '',
                        is_active: true
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={contractorsLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: contractorsLoading ? 'not-allowed' : 'pointer',
                      opacity: contractorsLoading ? 0.7 : 1
                    }}
                  >
                    {contractorsLoading ? 'جاري الحفظ...' : 'حفظ'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {/* Modal لإضافة مشروع جديد */}
      {
        showAddProjectModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }} onClick={() => setShowAddProjectModal(false)}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
                <button
                  onClick={() => {
                    setShowAddProjectModal(false);
                    setProjectForm({
                      code: '',
                      name_ar: '',
                      name_en: '',
                      customer_id: null,
                      start_date: '',
                      end_date: '',
                      status: 'planned',
                      contract_value: '',
                      tax_id: null,
                      cost_center_id: null,
                      notes: '',
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddProject}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.code', { defaultValue: 'الكود' })}</label>
                    <input type="text" name="code" value={projectForm.code} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                    <input type="text" name="name_ar" value={projectForm.name_ar} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}</label>
                    <input type="text" name="name_en" value={projectForm.name_en} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                  </div>
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div data-dropdown-toggle onClick={() => { setCustomerDropdownOpen((p) => !p); if (customerDropdownOpen) setCustomerSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                        <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}</span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddCustomerModal(true)}
                        style={{
                          backgroundColor: '#AC2000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '44px',
                          height: '44px'
                        }}
                        title="إضافة عميل جديد"
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch isOpen={customerDropdownOpen} onToggle={setCustomerDropdownOpen} searchTerm={customerSearchTerm} onSearchChange={setCustomerSearchTerm} searchInputRef={customerSearchInputRef} items={Array.isArray(customers) ? customers : []} pagination={customersPagination} currentPage={customerCurrentPage} onPageChange={setCustomerCurrentPage} onSelect={(id) => { setProjectForm((prev) => ({ ...prev, customer_id: id })); setCustomerDropdownOpen(false); setCustomerSearchTerm(''); setCustomerCurrentPage(1); }} displayValue={() => projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })} placeholder={t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })} label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                    <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                    <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                    <select name="status" value={projectForm.status} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                      <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                      <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                      <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                      <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                    <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                  </div>
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <div data-dropdown-toggle onClick={() => { setTaxDropdownOpen((p) => !p); if (taxDropdownOpen) setTaxSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                        <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.tax_id ? (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)) ? (isRTL ? ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || '') : ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || '')) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' }) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddTaxModal(true)}
                        style={{
                          backgroundColor: '#AC2000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '44px',
                          height: '44px'
                        }}
                        title={t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch isOpen={taxDropdownOpen} onToggle={setTaxDropdownOpen} searchTerm={taxSearchTerm} onSearchChange={setTaxSearchTerm} searchInputRef={taxSearchInputRef} items={Array.isArray(taxes) ? taxes : []} pagination={taxesPagination} currentPage={taxCurrentPage} onPageChange={setTaxCurrentPage} onSelect={(id) => { setProjectForm((prev) => ({ ...prev, tax_id: id })); setTaxDropdownOpen(false); setTaxSearchTerm(''); setTaxCurrentPage(1); }} displayValue={() => projectForm.tax_id ? (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)) ? (isRTL ? ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || '') : ((taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_en || (taxes || []).find(t => String(t.id) === String(projectForm.tax_id)).name_ar || '')) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' }) : t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' })} placeholder={t('quotations.add.modals.addProject.selectTax', { defaultValue: 'اختر الضريبة' })} label={t('quotations.add.modals.addProject.tax', { defaultValue: 'ضريبة' })} />
                  </div>
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <div data-dropdown-toggle onClick={() => { setCostCenterDropdownOpen((p) => !p); if (costCenterDropdownOpen) setCostCenterSearchTerm(''); }} style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }}>
                        <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{projectForm.cost_center_id ? (costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)) ? renderDropdownItemLabel((costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' }) : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                        <span style={{ fontSize: 12 }}>▼</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddCostCenterModal(true)}
                        style={{
                          backgroundColor: '#AC2000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '44px',
                          height: '44px'
                        }}
                        title={t('quotations.add.modals.addProject.addCostCenter', { defaultValue: 'إضافة مركز تكلفة جديد' })}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <DropdownWithSearch isOpen={costCenterDropdownOpen} onToggle={setCostCenterDropdownOpen} searchTerm={costCenterSearchTerm} onSearchChange={setCostCenterSearchTerm} searchInputRef={costCenterSearchInputRef} items={Array.isArray(costCenters) ? costCenters : []} pagination={costCentersPagination} currentPage={costCenterCurrentPage} onPageChange={setCostCenterCurrentPage} onSelect={(id) => { setProjectForm((prev) => ({ ...prev, cost_center_id: id })); setCostCenterDropdownOpen(false); setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }} displayValue={() => projectForm.cost_center_id ? (costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)) ? renderDropdownItemLabel((costCenters || []).find(cc => String(cc.id) === String(projectForm.cost_center_id)), 'code', 'name_ar', 'name') : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' }) : t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })} placeholder={t('quotations.add.modals.addProject.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })} label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز تكلفة' })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}</label>
                    <textarea ref={projectNotesTextareaRef} name="notes" value={projectForm.notes} onChange={handleProjectFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="button" onClick={() => {
                    setShowAddProjectModal(false);
                    setProjectForm({
                      code: '',
                      name_ar: '',
                      name_en: '',
                      customer_id: null,
                      start_date: '',
                      end_date: '',
                      status: 'planned',
                      contract_value: '',
                      tax_id: null,
                      cost_center_id: null,
                      notes: '',
                    });
                  }} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>{t('quotations.add.modals.addProject.cancel', { defaultValue: 'إلغاء' })}</button>
                  <button type="submit" disabled={isLoading} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1 }}>{isLoading ? t('quotations.add.modals.addProject.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addProject.save', { defaultValue: 'حفظ' })}</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {/* Modal لإضافة عميل جديد */}
      {
        showAddCustomerModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }} onClick={() => setShowAddCustomerModal(false)}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCustomer.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
                <button
                  onClick={() => {
                    setShowAddCustomerModal(false);
                    setCustomerForm({
                      name_en: '',
                      name_ar: '',
                      code: '',
                      contact_person: '',
                      email: '',
                      phone: '',
                      address: '',
                      tax_number: '',
                      is_active: true,
                      notes: ''
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddCustomer}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={customerForm.name_en}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={customerForm.name_ar}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.code', { defaultValue: 'الكود' })}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={customerForm.code}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      value={customerForm.contact_person}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.email', { defaultValue: 'البريد الإلكتروني' })}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={customerForm.email}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.phone', { defaultValue: 'الهاتف' })}
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={customerForm.phone}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.address', { defaultValue: 'العنوان' })}
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={customerForm.address}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                    </label>
                    <input
                      type="text"
                      name="tax_number"
                      value={customerForm.tax_number}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={customerForm.is_active}
                      onChange={handleCustomerFormChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.isActive', { defaultValue: 'نشط' })}
                    </label>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCustomer.notes', { defaultValue: 'ملاحظات' })}
                    </label>
                    <textarea
                      name="notes"
                      value={customerForm.notes}
                      onChange={handleCustomerFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        resize: 'vertical',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      setCustomerForm({
                        name_en: '',
                        name_ar: '',
                        code: '',
                        contact_person: '',
                        email: '',
                        phone: '',
                        address: '',
                        tax_number: '',
                        is_active: true,
                        notes: ''
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {t('quotations.add.modals.addCustomer.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1
                    }}
                  >
                    {isLoading ? t('quotations.add.modals.addCustomer.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCustomer.save', { defaultValue: 'حفظ' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {
        showAddTaxModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }} onClick={() => {
            setShowAddTaxModal(false);
            setTaxForm({
              name_ar: '',
              name_en: '',
              rate: '',
              is_active: true,
            });
          }}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaxModal(false);
                    setTaxForm({
                      name_ar: '',
                      name_en: '',
                      rate: '',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: 0,
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddTax}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={taxForm.name_ar}
                      onChange={handleTaxFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={taxForm.name_en}
                      onChange={handleTaxFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة %' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="number"
                      name="rate"

                      value={taxForm.rate}
                      onChange={handleTaxFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={taxForm.is_active}
                      onChange={handleTaxFormChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTaxModal(false);
                      setTaxForm({
                        name_ar: '',
                        name_en: '',
                        rate: '',
                        is_active: true,
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {t('quotations.add.modals.addTax.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1
                    }}
                  >
                    {isLoading ? t('quotations.add.modals.addTax.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addTax.save', { defaultValue: 'حفظ' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {
        showAddCostCenterModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }} onClick={() => {
            setShowAddCostCenterModal(false);
            setCostCenterForm({
              name_ar: '',
              name_en: '',
              code: '',
            });
          }}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: 0,
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddCostCenter}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={costCenterForm.name_ar}
                      onChange={handleCostCenterFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={costCenterForm.name_en}
                      onChange={handleCostCenterFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={costCenterForm.code}
                      onChange={handleCostCenterFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCostCenterModal(false);
                      setCostCenterForm({
                        name_ar: '',
                        name_en: '',
                        code: '',
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {t('quotations.add.modals.addCostCenter.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1
                    }}
                  >
                    {isLoading ? t('quotations.add.modals.addCostCenter.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCostCenter.save', { defaultValue: 'حفظ' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {/* Modal لإضافة وحدة جديدة */}
      {
        showAddUnitModal && ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 999999,
              padding: '20px'
            }}
            onMouseDown={(e) => {
              // Only close if clicking directly on the backdrop, not on child elements
              if (e.target === e.currentTarget) {
                setShowAddUnitModal(false);
                setAddUnitModalItemIndex(null);
                setUnitForm({
                  name_ar: '',
                  name_en: '',
                  symbol: '',
                  is_active: true,
                });
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#1b3350',
                borderRadius: '12px',
                padding: '30px',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflowY: 'auto',
                border: '1px solid #333',
                direction: isRTL ? 'rtl' : 'ltr'
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addUnit.title', { defaultValue: 'إضافة وحدة جديدة' })}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUnitModal(false);
                    setAddUnitModalItemIndex(null);
                    setUnitForm({
                      name_ar: '',
                      name_en: '',
                      symbol: '',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: 0,
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddUnit} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addUnit.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={unitForm.name_ar}
                      onChange={handleUnitFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addUnit.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={unitForm.name_en}
                      onChange={handleUnitFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addUnit.symbol', { defaultValue: 'الرمز' })} <span style={{ color: '#6b7280', fontSize: 12 }}>(اختياري)</span>
                    </label>
                    <input
                      type="text"
                      name="symbol"
                      value={unitForm.symbol}
                      onChange={handleUnitFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={unitForm.is_active}
                      onChange={handleUnitFormChange}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addUnit.isActive', { defaultValue: 'نشط' })}
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddUnitModal(false);
                      setAddUnitModalItemIndex(null);
                      setUnitForm({
                        name_ar: '',
                        name_en: '',
                        symbol: '',
                        is_active: true,
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {t('quotations.add.modals.addUnit.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1
                    }}
                  >
                    {isLoading ? t('quotations.add.modals.addUnit.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addUnit.save', { defaultValue: 'حفظ' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }

      {
        showAddCurrencyModal && ReactDOM.createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }} onClick={() => {
            setShowAddCurrencyModal(false);
            setCurrencyForm({
              name_en: '',
              name_ar: '',
              code: '',
              symbol: '',
              is_base_currency: false,
            });
          }}>
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addCurrency.title', { defaultValue: 'إضافة عملة جديدة' })}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCurrencyModal(false);
                    setCurrencyForm({
                      name_en: '',
                      name_ar: '',
                      code: '',
                      symbol: '',
                      is_base_currency: false,
                    });
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddCurrency}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCurrency.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={currencyForm.name_ar}
                      onChange={handleCurrencyFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCurrency.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name_en"
                      value={currencyForm.name_en}
                      onChange={handleCurrencyFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCurrency.code', { defaultValue: 'الكود' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={currencyForm.code.toUpperCase()}
                      onChange={handleCurrencyFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCurrency.symbol', { defaultValue: 'الرمز' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="symbol"
                      value={currencyForm.symbol}
                      onChange={handleCurrencyFormChange}
                      onFocus={(e) => e.stopPropagation()}
                      onBlur={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        direction: isRTL ? 'rtl' : 'ltr',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <input
                      type="checkbox"
                      name="is_base_currency"
                      checked={currencyForm.is_base_currency}
                      onChange={handleCurrencyFormChange}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addCurrency.isBaseCurrency', { defaultValue: 'عملة أساسية' })}
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCurrencyModal(false);
                      setCurrencyForm({
                        name_en: '',
                        name_ar: '',
                        code: '',
                        symbol: '',
                        is_base_currency: false,
                      });
                    }}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {t('quotations.add.modals.addCurrency.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '12px 25px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1
                    }}
                  >
                    {isLoading ? t('quotations.add.modals.addCurrency.saving', { defaultValue: 'جاري الحفظ...' }) : t('quotations.add.modals.addCurrency.save', { defaultValue: 'حفظ' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )
      }
    </div >
  );
};

export default AddQuotation;

